'use client';

import EastIcon from '@mui/icons-material/East';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  BottomSheet,
  StatCardLongShortProps,
  StatCardProps,
  TextTimer,
} from '@ui';
import { fromUnixTime } from 'date-fns';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useState } from 'react';
import { getPreRecommendationDetails } from '../services/recommendation';
import { useTokens } from '../store/auth/useTokens';
import useAppMeta from '../store/cycles/useAppMeta';
import useBuyCycle from '../store/cycles/useBuyCycle';
import queryClient from '../store/queryClient';
import { useBothUserPlans } from '../store/subscribe/usePlans';
import { useBrokerLearn } from '../store/useLearn';
import { useMixedPanel } from '../store/useMixPanel';
import { AppMeta } from '../types/appMeta';
import { formatRelativeOrAbsoluteDateTime } from '../utils/common';
import { Body, Caption, SubTitle } from './heading';
const StatCardGroup = () => {
  const [bottomSheetOpen, setBottomSheetOpen] = useState<
    'longOrShort' | 'long' | 'short' | 'afterHours' | 'basketShrunk' | undefined
  >(undefined);

  const themes = useTheme();
  const params = useSearchParams();
  const { push } = useRouter();
  const [{ data: meta }, { setTransactionType }] = useAppMeta();
  const plans = useBothUserPlans();
  const { trackEvent } = useMixedPanel();
  const {
    data: brokerLearnData,
    isLoading: isBrokerConfigLoading,
    error: brokerLearnError,
  } = useBrokerLearn(
    useTokens.getState().userInfo?.broker?.toLowerCase(),
    'intraday',
    'FAQ'
  );

  const { data: preRecommendationDetails, isLoading } = useQuery({
    queryKey: ['pre-recommendation-details'],
    queryFn: () =>
      getPreRecommendationDetails(
        params?.get('live') === 'false' ? false : true
      ),
  });
  const [buyCycle, setBuyCycle] = useBuyCycle();
  const onBuyClick = useCallback(
    (type: 'long' | 'short') => {
      setBottomSheetOpen(undefined);
      setBuyCycle(null, type === 'long' ? 'hunter_long' : 'hunter_short');

      if (!plans[type]?.skipPayment) {
        const params = new URLSearchParams({
          type: type == 'long' ? 'hunter_long' : 'hunter_short',
        });
        trackEvent('Navigate to subscribe', { type });
        push(`/subscribe?${params}`);
        return;
      }

      if (!preRecommendationDetails) {
        return;
      }
      setTransactionType(type === 'long' ? 'hunter_long' : 'hunter_short');
      trackEvent('CLICK: Buy Basket', { type });

      if (
        preRecommendationDetails?.shortStatus === 'basket_shrunk' &&
        type === 'short'
      ) {
        trackEvent('Basket Shrunk', { type });
        setBottomSheetOpen('basketShrunk');
        return;
      }
      if (
        preRecommendationDetails?.longStatus === 'basket_shrunk' &&
        type === 'long'
      ) {
        trackEvent('Basket Shrunk', { type });
        setBottomSheetOpen('basketShrunk');
        return;
      }
      if (
        preRecommendationDetails?.shortStatus === 'after_hours' ||
        preRecommendationDetails?.longStatus === 'after_hours'
      ) {
        trackEvent('Buy After Hours', { type });
        setBottomSheetOpen('afterHours');
        return;
      }
      const meta = queryClient.getQueryData<AppMeta>(['meta']);
      if (plans[type]?.skipPayment && !!meta?.transactionType) {
        trackEvent('Skip Payment', { type });
        push(`/configure?type=${meta?.transactionType}`);
        return;
      }
    },
    [
      preRecommendationDetails,
      setTransactionType,
      trackEvent,
      plans,
      meta,
      push,
    ]
  );

  const getTitle = useCallback(() => {
    const dateAndTime = fromUnixTime(
      preRecommendationDetails?.nextRecommendationTime ?? 0
    );

    const titleMap: Record<string, string | JSX.Element> = {
      long: 'Long Basket',
      short: 'Short Basket',
      afterHours: (
        <>
          <SubTitle>Recommendations Unavailable</SubTitle>
          <Caption>
            Next recommendations available in{' '}
            <TextTimer endTime={dateAndTime ?? 0} />
          </Caption>
        </>
      ),
      basketShrunk: (
        <>
          <SubTitle>Recommendations Unavailable</SubTitle>
          <Caption>
            Next recommendations available in{' '}
            <TextTimer endTime={dateAndTime ?? 0} />
          </Caption>
        </>
      ),
    };

    return titleMap[bottomSheetOpen as keyof typeof titleMap] || '';
  }, [bottomSheetOpen, preRecommendationDetails]);

  const getBottomSheetContent = () => {
    const dateAndTime = fromUnixTime(
      preRecommendationDetails?.nextRecommendationTime ?? 0
    );
    const contentMap: Record<string, JSX.Element> = {
      longOrShort: (
        <>
          <Stack>
            <SubTitle>Long</SubTitle>
            <Typography>
              A basket of 3-4 stocks identified by the model as having high
              potential to increase in price through the day.
            </Typography>
          </Stack>
          <Stack mt={3}>
            <SubTitle>Short</SubTitle>
            <Typography>
              A basket of 3-4 stocks identified by the model as having high
              potential to decrease in price through the day.
            </Typography>
          </Stack>
        </>
      ),
      afterHours: (
        <>
          <Typography>
            Recommendations are only available between 9:30 AM and 11:30 AM on
            trading days.
          </Typography>
          <Typography>
            Stay tuned for fresh recommendations{' '}
            {formatRelativeOrAbsoluteDateTime(dateAndTime)}.
          </Typography>
          <Stack
            sx={{ alignItems: 'center', justifyContent: 'center' }}
          ></Stack>
        </>
      ),
      basketShrunk: (
        <>
          <Typography variant="body2">
            Due to market conditions, there are no recommendations currently.
          </Typography>
          <Typography variant="body2">
            Expect the next set of recommendations{' '}
            {formatRelativeOrAbsoluteDateTime(dateAndTime)}.
          </Typography>
          <Stack
            sx={{ alignItems: 'center', justifyContent: 'center' }}
          ></Stack>
        </>
      ),
      default: <Typography></Typography>,
    };

    return contentMap[bottomSheetOpen ?? 'default'];
  };

  const statCardTheme: Pick<StatCardProps, 'theme'> = {
    theme: {
      buttonSx: {
        minWidth: '160px',
      },
    },
  };

  const StatCardLongShortTheme: Pick<StatCardLongShortProps, 'theme'> = {
    theme: {},
  };

  if (isLoading) {
    return <Skeleton height={400} />;
  }

  return (
    <>
      <Stack
        sx={{
          flexWrap: 'wrap',
          flexDirection: 'row',
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: '1 0 calc(50% - 8px)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 2,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'grey.100',
            },
          }}
          onClick={() => {
            trackEvent('CLICK: Past baskets');
            push(`/past-basket?${params}`);
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Past Baskets
          </Typography>
          <EastIcon sx={{ color: themes?.palette?.primary?.main }} />
        </Box>
      </Stack>
      <BottomSheet
        isOpen={!!bottomSheetOpen}
        showCloseButton={bottomSheetOpen == 'longOrShort'}
        header={<SubTitle>{getTitle()}</SubTitle>}
        onClose={() => {
          setBottomSheetOpen(undefined);
        }}
        theme={{
          bottomSheetSx: {
            padding: 2,
          },
          closeButtonSx: {
            color: 'primary.main',
          },
        }}
        content={<Body mt={0}>{getBottomSheetContent()}</Body>}
        footer={
          <>
            {bottomSheetOpen === 'longOrShort' ? (
              <Stack direction={'row'} width={'100%'} spacing={2} mt={2}>
                <Button
                  variant="contained"
                  fullWidth
                  color="success"
                  sx={{
                    backgroundColor: '#028715',
                    borderRadius: '8px',
                  }}
                  onClick={() => {
                    bottomSheetOpen === 'longOrShort'
                      ? onBuyClick('long')
                      : setBottomSheetOpen(undefined);
                  }}
                >
                  Long
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  sx={{
                    backgroundColor: '#EE1819',
                    borderRadius: '8px',
                  }}
                  onClick={() => {
                    bottomSheetOpen === 'longOrShort'
                      ? onBuyClick('short')
                      : setBottomSheetOpen(undefined);
                  }}
                >
                  Short
                </Button>
              </Stack>
            ) : (
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  setBottomSheetOpen(undefined);
                }}
              >
                Close
              </Button>
            )}
          </>
        }
      />
    </>
  );
};

export default StatCardGroup;
